import { useEffect, useMemo, useRef, useState } from 'react';
import { parseCadJsonFile, type CadJsonFile, type CadJsonPayload } from '@/api/cadJson';
import { useAuthStore } from '@/stores/auth';

interface CadJsonPickerProps {
  projectId: number;
  title?: string;
  onSelect: (payload: CadJsonPayload) => void;
  onClose: () => void;
}

export function CadJsonPicker({ projectId, title = 'CAD JSON kiezen', onSelect, onClose }: CadJsonPickerProps) {
  const [files, setFiles] = useState<CadJsonFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentScan, setCurrentScan] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [parsingPath, setParsingPath] = useState<string | null>(null);
  const [incomplete, setIncomplete] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const projectFolderRef = useRef<string | null>(null);

  useEffect(() => {
    const token = useAuthStore.getState().token;
    if (!token) {
      setError('Niet ingelogd');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setFiles([]);
    setCurrentScan(null);
    setIncomplete(false);

    const baseUrl = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');
    const es = new EventSource(`${baseUrl}/api/projects/${projectId}/cad-json/stream?token=${encodeURIComponent(token)}`);

    const seen = new Set<string>();

    es.addEventListener('start', (e) => {
      try {
        const data = JSON.parse((e as MessageEvent).data) as { projectFolder?: string };
        projectFolderRef.current = data.projectFolder ?? null;
      } catch { /* ignore */ }
    });

    es.addEventListener('status', (e) => {
      try {
        const data = JSON.parse((e as MessageEvent).data) as { message?: string };
        if (data?.message) setCurrentScan(data.message);
      } catch { /* ignore */ }
    });

    es.addEventListener('file', (e) => {
      try {
        const file = JSON.parse((e as MessageEvent).data) as CadJsonFile;
        if (seen.has(file.path)) return;
        seen.add(file.path);
        setFiles((prev) => [...prev, file]);
      } catch { /* ignore */ }
    });

    es.addEventListener('scanning', (e) => {
      try {
        const data = JSON.parse((e as MessageEvent).data) as { path: string };
        setCurrentScan(displayFolder(data.path, projectFolderRef.current));
      } catch { /* ignore */ }
    });

    es.addEventListener('scan-error', () => {
      // A folder failed to list (throttle/timeout). The scan continues, but the
      // result may be partial — flag it so the UI never lies "no files found".
      setIncomplete(true);
    });

    es.addEventListener('incomplete', () => {
      setIncomplete(true);
    });

    es.addEventListener('done', () => {
      setLoading(false);
      setCurrentScan(null);
      es.close();
    });

    es.addEventListener('error', (e) => {
      try {
        const data = JSON.parse((e as MessageEvent).data) as { message?: string };
        if (data?.message) setError(data.message);
      } catch { /* ignore */ }
      setLoading(false);
      setCurrentScan(null);
      es.close();
    });

    return () => { es.close(); };
  }, [projectId, retryKey]);

  const sortedFiles = useMemo(() => {
    return [...files].sort((a, b) => (b.lastModified ?? '').localeCompare(a.lastModified ?? ''));
  }, [files]);

  async function handleSelect(file: CadJsonFile) {
    setParsingPath(file.path);
    setError(null);
    try {
      const payload = await parseCadJsonFile(projectId, file.path);
      onSelect(payload);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'CAD JSON parsen mislukt');
    } finally {
      setParsingPath(null);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-3xl rounded-xl bg-white shadow-xl border border-gray-200">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="text-base font-bold text-td-navy">{title}</h3>
          <button type="button" onClick={onClose} className="text-td-muted hover:text-td-navy" aria-label="Sluiten">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-5">
          {error && (
            <div className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600" role="alert">
              {error}
            </div>
          )}

          {loading && sortedFiles.length === 0 ? (
            <div className="py-10 text-center text-sm text-td-muted">
              <div className="mx-auto mb-3 h-7 w-7 rounded-full border-4 border-td-blue/30 border-t-td-blue animate-spin" />
              <p>Projectmap doorzoeken...</p>
              {currentScan && <p className="mt-1 text-xs text-td-muted/70 truncate">📂 {currentScan}</p>}
            </div>
          ) : !loading && sortedFiles.length === 0 && incomplete ? (
            <div className="py-10 text-center text-sm text-td-muted">
              <p className="text-amber-700">Doorzoeken onvolledig — SharePoint is mogelijk overbelast.</p>
              <p className="mt-1 text-xs text-td-muted/70">De projectmap kon niet volledig doorzocht worden.</p>
              <button
                type="button"
                onClick={() => setRetryKey((k) => k + 1)}
                className="mt-4 rounded-lg bg-td-blue px-4 py-2 text-xs font-semibold text-white hover:bg-td-blue/90"
              >
                Opnieuw proberen
              </button>
            </div>
          ) : !loading && sortedFiles.length === 0 ? (
            <p className="py-10 text-center text-sm text-td-muted">Geen CAD JSON bestanden gevonden.</p>
          ) : (
            <div className="max-h-[60vh] overflow-auto rounded-lg border border-gray-200">
              {!loading && incomplete && (
                <div className="flex items-center gap-2 bg-amber-50 px-3 py-2 text-xs text-amber-700 border-b border-amber-100" role="alert">
                  <span className="truncate">
                    ⚠️ Doorzoeken was onvolledig (SharePoint druk) — de lijst is mogelijk niet compleet.
                  </span>
                  <button
                    type="button"
                    onClick={() => setRetryKey((k) => k + 1)}
                    className="ml-auto shrink-0 rounded bg-amber-100 px-2 py-1 font-semibold text-amber-800 hover:bg-amber-200"
                  >
                    Opnieuw
                  </button>
                </div>
              )}
              {loading && (
                <div className="flex items-center gap-2 bg-td-blue/5 px-3 py-2 text-xs text-td-blue border-b border-td-blue/10">
                  <div className="h-3 w-3 rounded-full border-2 border-td-blue/30 border-t-td-blue animate-spin" />
                  <span className="truncate">
                    {currentScan ? `Doorzoeken: ${currentScan}` : 'Nog meer mappen doorzoeken...'}
                  </span>
                </div>
              )}
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-[10px] font-bold uppercase tracking-wide text-td-muted">
                  <tr>
                    <th className="px-3 py-2">Bestand</th>
                    <th className="px-3 py-2 whitespace-nowrap">Laatst gewijzigd</th>
                    <th className="px-3 py-2 text-right">Grootte</th>
                    <th className="px-3 py-2 w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedFiles.map((file) => {
                    const isParsing = parsingPath === file.path;
                    return (
                      <tr key={file.path} className="border-t border-gray-100">
                        <td className="px-3 py-2 text-td-navy">{file.name}</td>
                        <td className="px-3 py-2 text-td-muted whitespace-nowrap">{formatDate(file.lastModified)}</td>
                        <td className="px-3 py-2 text-right text-td-muted">{formatBytes(file.sizeBytes)}</td>
                        <td className="px-3 py-2 text-right">
                          <button
                            type="button"
                            onClick={() => handleSelect(file)}
                            disabled={!!parsingPath}
                            className="rounded-lg bg-td-blue px-3 py-1.5 text-xs font-semibold text-white hover:bg-td-blue/90 disabled:opacity-50"
                          >
                            {isParsing ? 'Laden...' : 'Kies'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function formatDate(value?: string): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('nl-BE', { dateStyle: 'short', timeStyle: 'short' });
}

function formatBytes(value?: number): string {
  if (typeof value !== 'number') return '-';
  if (value < 1024) return `${value} B`;
  return `${Math.round(value / 1024)} KB`;
}

function displayFolder(path: string, projectFolder: string | null): string {
  if (!projectFolder || !path.startsWith(projectFolder)) return path;
  const tail = path.slice(projectFolder.length).replace(/^\//, '');
  return tail || '(root)';
}
